import {
  anchorDisplayDate,
  formatDisplayDate,
  resolveLocalized,
  toCanonicalDay,
  toDisplayDate,
  type Calendar,
  type CanonicalDay,
  type DisplayDate,
} from '@dl/calendar';
import { div, input, label, span, type Mountable } from '@llui/dom';

import { selectInput } from './atoms/select-input.js';

/**
 * `<CalendarInput>` — a calendar-aware date picker.
 *
 * Phase 1 scope (issue #56 II.C.4):
 *
 * - Accepts an active `Calendar` from `@dl/calendar` and an integer
 *   `value` (a canonical day count) plus an `onChange` callback. Renders
 *   four native `<select>` elements bound to (era, year, month, day) so
 *   keyboard / screen-reader / mobile native picker behaviour is free.
 * - Displays the formatted date as a sibling read string (via the
 *   calendar's `formatDisplayDate`), so the picker doubles as the
 *   reader view (per the "editor view equals reader view" invariant in
 *   CLAUDE.md).
 * - Read-only mode (`mode: 'read'`) renders ONLY the formatted string —
 *   same DOM root, ARIA-equivalent. Toggling between modes does not
 *   change the visual hierarchy.
 *
 * Out of scope for Phase 1:
 *
 * - Month grid view (Phase 2 — the mock-up surfaces in `mocks/`).
 * - Sub-day precision pickers (Phase 2, `06-timeline.md §6`).
 * - Recurring-date authoring (Phase 2, `06-timeline.md §10`).
 *
 * Localisation:
 *
 *   All labels accept an optional `locale` (defaults to 'en') and an
 *   optional `messages` catalog that the host can override. The catalog
 *   below is the English baseline — apps that already maintain a
 *   `t()` table can pass its accessor as the `messages` adapter.
 */

export type CalendarInputMode = 'edit' | 'read';

/**
 * How the era selector labels its options. `full` (default) shows the
 * disambiguated label ("Common Era (CE)") for spacious pickers; `abbreviation`
 * shows just the abbreviation ("CE") and a narrower select, for compact inline
 * contexts like the as-of browse scrubber.
 */
export type EraStyle = 'full' | 'abbreviation';

/** Catalog of localizable strings used by the picker. Hosts override to integrate with their `t()`. */
export interface CalendarInputMessages {
  /** ARIA label for the era selector. */
  era: string;
  /** ARIA label for the year input. */
  year: string;
  /** ARIA label for the month selector. */
  month: string;
  /** ARIA label for the day selector. */
  day: string;
  /** ARIA label for the "clear" button (open-ended bound). */
  clear: string;
  /** Text shown when the value is `null` (open-ended). */
  unset: string;
  /** Label for the "set a date" button shown when value is null and the user wants to re-engage. */
  setDate?: string;
}

export const DEFAULT_CALENDAR_INPUT_MESSAGES: CalendarInputMessages = {
  era: 'Era',
  year: 'Year',
  month: 'Month',
  day: 'Day',
  clear: 'Clear',
  unset: '—',
  setDate: 'Set a date',
};

export interface CalendarInputOptions {
  /** Active calendar (from `@dl/calendar`'s pack loader). */
  calendar: Calendar;
  /**
   * Current canonical day, or `null` for an open-ended bound. Accepts a
   * raw axis `number` at the boundary (reading a day value is not the
   * "bare arithmetic" ADR-0006 forbids); `onChange` always emits a
   * branded `CanonicalDay`.
   */
  value: CanonicalDay | number | null;
  /** Called when the user picks a new date. `null` means the user cleared the bound. */
  onChange: (next: CanonicalDay | null) => void;
  /** `edit` shows the picker; `read` shows only the formatted string. Defaults to `edit`. */
  mode?: CalendarInputMode;
  /** How the era selector labels its options. Defaults to `full`. */
  eraStyle?: EraStyle;
  /** Whether the bound is allowed to be cleared (open-ended). Defaults to `true`. */
  clearable?: boolean;
  /** Stable DOM id prefix for the inputs (so labels match). Required for a11y. */
  id: string;
  /** Display locale for labels. Defaults to 'en'. */
  locale?: string;
  /** Optional message catalog override. */
  messages?: Partial<CalendarInputMessages>;
  /** Optional `data-testid` prefix to ease E2E testing. */
  testid?: string;
}

interface PickerParts {
  era: string;
  year: number;
  month: number;
  day: number;
}

/**
 * Resolve the calendar's set of selectable eras. Order is preserved from
 * the calendar definition so the dropdown matches the pack author's
 * intent (BCE before CE, NR before DR, etc.).
 */
function eraOptions(
  calendar: Calendar,
  locale: string,
  eraStyle: EraStyle,
): { id: string; label: string }[] {
  return calendar.eras.map((e) => {
    const labelText =
      (e.label !== undefined ? resolveLocalized(e.label, locale) : '') || e.name;
    // Compact contexts (e.g. an inline browse pill) show just the
    // abbreviation ("CE" / "BCE") so the era select doesn't dominate;
    // the spacious default keeps the full disambiguated label.
    if (eraStyle === 'abbreviation' && e.abbreviation !== undefined) {
      return { id: e.id, label: e.abbreviation };
    }
    return { id: e.id, label: e.abbreviation !== undefined ? `${labelText} (${e.abbreviation})` : labelText };
  });
}

function monthOptions(
  calendar: Calendar,
  locale: string,
): { index: number; label: string; intercalary: boolean }[] {
  return calendar.cycles.year.months.map((m, i) => ({
    index: i + 1,
    label: m.label !== undefined ? resolveLocalized(m.label, locale) : m.name,
    intercalary: m.intercalary === true,
  }));
}

/** Last day available in `(year, monthIndex)` under the calendar's leap rule. */
function maxDayOfMonth(calendar: Calendar, year: number, monthIndex: number): number {
  const months = calendar.cycles.year.months;
  const m = months[monthIndex - 1];
  if (!m) return 1;
  // We can't import isLeapYear here without widening the @dl/calendar
  // surface; use the calendar engine itself by attempting both lengths.
  // (Cheap: a single toCanonicalDay rejection vs. accepted call.)
  if (m.leap_length !== undefined) {
    const probe = toCanonicalDay(calendar, { era: defaultEraId(calendar), year, month: monthIndex, day: m.leap_length });
    if (probe.ok) return m.leap_length;
  }
  return m.length;
}

function defaultEraId(calendar: Calendar): string {
  // Prefer the era covering the calendar anchor (ADR-0006 §2); fall back
  // to the first declared era.
  const anchor = anchorDisplayDate(calendar);
  if (anchor.ok) return anchor.value.era;
  return calendar.eras[0]?.id ?? '';
}

/**
 * Resolve the canonical day to a four-tuple suitable for the picker
 * selects. If `value` is `null` (or invalid), returns the calendar's
 * anchor date as a neutral start position (the picker UI still draws
 * something) — ADR-0006 §2 replaces the former `calendar.epoch`.
 */
function partsFor(calendar: Calendar, value: CanonicalDay | number | null): PickerParts {
  const neutral = ((): PickerParts => {
    const anchor = anchorDisplayDate(calendar);
    if (anchor.ok) {
      const a = anchor.value;
      return { era: a.era, year: a.year, month: a.month, day: a.day };
    }
    const era0 = calendar.eras[0];
    return { era: era0?.id ?? '', year: era0?.year_one ?? 1, month: 1, day: 1 };
  })();
  if (value === null) return neutral;
  const result = toDisplayDate(calendar, value);
  if (result.ok) {
    const d = result.value;
    return { era: d.era, year: d.year, month: d.month, day: d.day };
  }
  return neutral;
}

/**
 * Build the `<CalendarInput>` DOM tree. Returns a single `Node` so the
 * host inserts it anywhere a fact value or validity chip is expected.
 *
 * Behavioural notes:
 *
 * - Every part-change (era, year, month, day) triggers `onChange` with
 *   the new canonical day, clamping the day-of-month so it stays valid
 *   under the leap rule (e.g. picking Feb 29 when switching from leap
 *   to non-leap snaps to Feb 28).
 * - If the resulting tuple does not convert to a canonical day
 *   (e.g. Shieldmeet in a non-leap Harptos year, or a BCE date past the
 *   era ceiling), `onChange` is NOT called and the previous valid
 *   value is left untouched.
 */
export function calendarInput(options: CalendarInputOptions): Mountable {
  const {
    calendar,
    value,
    onChange,
    id,
    locale = 'en',
    clearable = true,
    testid,
    mode = 'edit',
    eraStyle = 'full',
  } = options;
  const messages: CalendarInputMessages = { ...DEFAULT_CALENDAR_INPUT_MESSAGES, ...options.messages };

  // Read mode shows ONLY the formatted display string — this is the
  // GM's reader view of the date and matches the "editor view equals
  // reader view" invariant from CLAUDE.md. The formatted preview was
  // removed from EDIT mode because it duplicates what the picker
  // selects already render (user could read the date from the selects
  // themselves).
  if (mode === 'read') {
    const formatted =
      value === null
        ? messages.unset
        : (() => {
            const r = toDisplayDate(calendar, value);
            if (!r.ok) return messages.unset;
            return formatDisplayDate(calendar, r.value, { locale });
          })();
    return span(
      {
        class: 'dl-calendar-input dl-calendar-input--read',
        'aria-live': 'polite',
        ...(testid !== undefined ? { 'data-testid': testid } : {}),
      },
      [formatted],
    );
  }

  const parts = partsFor(calendar, value);
  const eras = eraOptions(calendar, locale, eraStyle);
  const months = monthOptions(calendar, locale);
  const dayMax = maxDayOfMonth(calendar, parts.year, parts.month);

  const handlePart = (next: Partial<PickerParts>): void => {
    const candidate: DisplayDate = {
      era: next.era ?? parts.era,
      year: next.year ?? parts.year,
      month: next.month ?? parts.month,
      day: Math.min(next.day ?? parts.day, maxDayOfMonth(calendar, next.year ?? parts.year, next.month ?? parts.month)),
    };
    const result = toCanonicalDay(calendar, candidate);
    if (result.ok) onChange(result.value);
  };

  // "Open-ended" is only a real state when the bound is clearable. A
  // non-clearable picker (e.g. the campaign cursor — a single in-world
  // instant that's never open-ended) must ALWAYS show the selects,
  // seeded at the calendar epoch when no date is set yet — otherwise a
  // null value renders an empty box with no way to pick (the bug that
  // made "pick a date" unusable).
  const isOpenEnded = value === null && clearable;

  // The control lays out on ONE row:
  //   [day] [month] [year] [era]   ☐ <open-ended>
  // The "open-ended" affordance is a checkbox at the trailing edge —
  // the familiar "☑ No end date" pattern from calendar apps. Checking
  // it clears the bound (open-ended); unchecking seeds the calendar
  // epoch so the picker re-engages. Reads as a real toggle, not the
  // floating caption text the old ghost button looked like. The
  // `-clear` testid is preserved so the toggle click path (→
  // onChange(null)) matches the prior clear-button contract.
  //
  // The picker selects render only when a date is set; when open-ended
  // they're omitted entirely (no misleading epoch numbers leaking
  // through). Both branches use conditional LITERAL-array spreads —
  // the compiler rejects spreading a dynamic variable array in
  // children (the template-clone optimisation needs static child
  // counts per branch).
  return div(
    {
      class: `dl-calendar-input dl-calendar-input--edit${isOpenEnded ? ' dl-calendar-input--unset' : ''}`,
      role: 'group',
      'aria-label': `${messages.day} ${messages.month} ${messages.year} ${messages.era}`,
      ...(testid !== undefined ? { 'data-testid': testid } : {}),
    },
    [
      ...(isOpenEnded
        ? []
        : [
            label({ for: `${id}-day`, class: 'dl-cal-vh' }, [messages.day]),
            selectInput({
              id: `${id}-day`,
              name: `${id}-day`,
              ariaLabel: messages.day,
              ...(testid !== undefined ? { testid: `${testid}-day` } : {}),
              value: String(parts.day),
              size: 'sm',
              options: Array.from({ length: dayMax }, (_, i) => ({
                value: String(i + 1),
                label: String(i + 1),
              })),
              onChange: (next) => {
                const raw = Number(next);
                if (Number.isFinite(raw)) handlePart({ day: raw });
              },
            }),
            label({ for: `${id}-month`, class: 'dl-cal-vh' }, [messages.month]),
            selectInput({
              id: `${id}-month`,
              name: `${id}-month`,
              ariaLabel: messages.month,
              ...(testid !== undefined ? { testid: `${testid}-month` } : {}),
              value: String(parts.month),
              size: 'md',
              options: months.map((m) => ({
                value: String(m.index),
                label: m.intercalary ? `${m.label} ✦` : m.label,
                ...(m.intercalary ? { dataAttrs: { 'data-intercalary': 'true' } } : {}),
              })),
              onChange: (next) => {
                const raw = Number(next);
                if (Number.isFinite(raw)) handlePart({ month: raw });
              },
            }),
            label({ for: `${id}-year`, class: 'dl-cal-vh' }, [messages.year]),
            input({
              id: `${id}-year`,
              name: `${id}-year`,
              type: 'number',
              inputmode: 'numeric',
              class: 'dl-cal-year-input',
              'aria-label': messages.year,
              ...(testid !== undefined ? { 'data-testid': `${testid}-year` } : {}),
              value: String(parts.year),
              onChange: (event: Event) => {
                const raw = Number((event.target as HTMLInputElement).value);
                if (Number.isFinite(raw) && Number.isInteger(raw)) handlePart({ year: raw });
              },
            }),
            label({ for: `${id}-era`, class: 'dl-cal-vh' }, [messages.era]),
            selectInput({
              id: `${id}-era`,
              name: `${id}-era`,
              ariaLabel: messages.era,
              ...(testid !== undefined ? { testid: `${testid}-era` } : {}),
              value: parts.era,
              // 'lg' so the full era label ("Common Era (CE)" / "Before
              // Common Era (BCE)") is readable without truncation; an
              // abbreviation-style era ("CE" / "BCE") needs only a narrow box.
              size: eraStyle === 'abbreviation' ? 'sm' : 'lg',
              options: eras.map((e) => ({ value: e.id, label: e.label })),
              onChange: (next) => handlePart({ era: next }),
            }),
          ]),
      ...(clearable
        ? [
            label(
              {
                class: 'dl-cal-openended',
                ...(testid !== undefined ? { 'data-testid': `${testid}-openended` } : {}),
              },
              [
                input({
                  type: 'checkbox',
                  class: 'dl-cal-openended-box',
                  checked: isOpenEnded,
                  'aria-label': messages.clear,
                  ...(testid !== undefined ? { 'data-testid': `${testid}-clear` } : {}),
                  onChange: (event: Event) => {
                    const checked = (event.target as HTMLInputElement).checked;
                    if (checked) {
                      onChange(null);
                      return;
                    }
                    // Re-engage: seed the calendar epoch as the start.
                    const seed = toCanonicalDay(calendar, {
                      era: parts.era,
                      year: parts.year,
                      month: parts.month,
                      day: parts.day,
                    });
                    if (seed.ok) onChange(seed.value);
                  },
                }),
                span({ class: 'dl-cal-openended-label' }, [messages.unset]),
              ],
            ),
          ]
        : []),
    ],
  );
}
